import BigNumber from 'bignumber.js';
import moment from 'moment';
import { isNullOrUndefined } from 'src/app/shared/utils';
import { DataDisplayOptions } from '../../dataModel';
import { DataType } from '../../dataModel/DataType';
import * as DataQueryExpr from '../expr';
import { Evaluator, EvaluatorOptions } from './Evaluator';
import { FunctionSymbol, IdentifierSymbol, SYMBOLS } from './Symbols';
import { Value } from './Value';

type SwitchOnArgsKey =
  number |
  { min: number, max?: number };

type SwitchOnArgsValue = (vm: VM, ...args: any[]) => any;
type SwitchOnArgsInput = { args: SwitchOnArgsKey, then: SwitchOnArgsValue };

export class VM {
  constructor(public readonly evaluator: Evaluator) { }


  readonly symbols = SYMBOLS;


  readonly stack: any[] = [];

  push(...values: any[]) {
    this.stack.push(...values);
    return this;
  }

  pop(count = 1) {
    while (count-- > 0)
      this.stack.pop();

    return this;
  }


  private _args: any[] = [];
  
  args(): any[];
  args(count: number, handlers?: Function[]): any[];
  args(input: SwitchOnArgsKey, handlers?: Function[]): any[];
  args(input: SwitchOnArgsInput[], handlers?: Function[]): any[];
  args(countOrInput?: number | SwitchOnArgsKey | SwitchOnArgsInput[], handlers?: Function[]): any[] {
    let thisArgs = this._args;

    if (typeof countOrInput === 'undefined')
      return thisArgs;

    if (typeof countOrInput === 'number') {
      let count = countOrInput;
      if (thisArgs.length !== count)
        throw new Error(`Invalid number of arguments`);

      return returnArgs();
    }

    let input = countOrInput;

    if (!Array.isArray(input) && ('min' in input)) {
      input = <{ min: number }>input;

      if (thisArgs.length >= input.min) {
        if (!('max' in input) || thisArgs.length <= input.max) {
          return returnArgs();
        }
      }
    }
    else {
      input = <SwitchOnArgsInput[]>input;

      let specificArgsOptions = input.filter(x => typeof x.args === 'number');
      for (let { args, then } of specificArgsOptions) {
        if (thisArgs.length === args) {
          return then(this, ...returnArgs());
        }
      }

      let rangeArgsOptions = input.filter(x => typeof x.args === 'object');
      for (let { args, then } of rangeArgsOptions) {
        let { min, max } = <any>args;

        if (typeof min === 'number') {
          if (thisArgs.length < min)
            continue;
        }

        if (typeof max === 'number') {
          if (thisArgs.length > max)
            continue;
        }

        return then(this, ...returnArgs());
      }

      let anyArgsOption = input.find(x => isNullOrUndefined(x.args));
      if (anyArgsOption) {
        return anyArgsOption.then(this);
      }
    }

    throw new Error(`Invalid number of arguments`);


    function returnArgs(count?: number) {
      if (!handlers)
        return thisArgs;

      let result = [];

      let lastHandlerIndex = handlers.length - 1;

      for (let i = 0; i < thisArgs.length; ++i) {
        let arg = thisArgs[Math.min(i, lastHandlerIndex)];
        let handler = handlers[i];
        if (handler) {
          result.push(handler.call(this, arg));
        } else {
          result.push(arg);
        }
      }

      return result;
    }
  }


  ident(identName: string);
  ident(ident: IdentifierSymbol);
  ident(identOrName: string | IdentifierSymbol) {
    let ident: IdentifierSymbol;

    if (typeof identOrName === 'string') {
      ident = SYMBOLS.getFunction(identOrName);
      if (!ident)
        throw new Error(`Unknown function '${identOrName}'`);
    }
    else
      ident = identOrName;

    return ident.handler(this);
  }


  call(funcName: string, ...args: any[]);
  call(func: FunctionSymbol, ...args: any[]);
  call(funcOrName: string | FunctionSymbol, ...args: any[]) {
    let func: FunctionSymbol;

    if (typeof funcOrName === 'string') {
      func = SYMBOLS.getFunction(funcOrName);
      if (!func)
        throw new Error(`Unknown function '${funcOrName}'`);
    }
    else
      func = funcOrName;

    let prevArgs = this._args;

    try {
      this._args = args;
      return func.handler(this);
    }
    finally {
      this._args = prevArgs;
    }
  }


  add(l: any, r: any): any {
    if (this.getPrecedence(l) < this.getPrecedence(r))
      return this.switchNumeric(r, {
        number: (vm, r, t) => vm.getNumber(l, t.isInteger) + r,
        bigNumber: (vm, r, t) => vm.getBigNumber(l, t.isInteger).plus(r),
      });
    else
      return this.switchNumeric(l, {
        number: (vm, l, t) => l + vm.getNumber(r, t.isInteger),
        bigNumber: (vm, l, t) => l.plus(vm.getBigNumber(r, t.isInteger)),
      });
  }

  sub(l: any, r: any): any {
    if (this.getPrecedence(l) < this.getPrecedence(r))
      return this.switchNumeric(r, {
        number: (vm, r, t) => vm.getNumber(l, t.isInteger) - r,
        bigNumber: (vm, r, t) => vm.getBigNumber(l, t.isInteger).minus(r),
      });
    else
      return this.switchNumeric(l, {
        number: (vm, l, t) => l - vm.getNumber(r, t.isInteger),
        bigNumber: (vm, l, t) => l.minus(vm.getBigNumber(r, t.isInteger)),
      });
  }

  mul(l: any, r: any): any {
    if (this.getPrecedence(l) < this.getPrecedence(r))
      return this.switchNumeric(r, {
        number: (vm, r, t) => vm.getNumber(l, t.isInteger) * r,
        bigNumber: (vm, r, t) => vm.getBigNumber(l, t.isInteger).multipliedBy(r),
      });
    else
      return this.switchNumeric(l, {
        number: (vm, l, t) => l * vm.getNumber(r, t.isInteger),
        bigNumber: (vm, l, t) => l.multipliedBy(vm.getBigNumber(r, t.isInteger)),
      });
  }

  div(l: any, r: any): any {
    if (this.getPrecedence(l) < this.getPrecedence(r))
      return this.switchNumeric(r, {
        number: (vm, r, t) => {
          if (vm.isZero(r))
            throw new VM.OperationError(VM.OperationErrorType.DivisionByZero);

          return vm.getNumber(l, t.isInteger) / r;
        },
        bigNumber: (vm, r, t) => {
          if (vm.isZero(r))
            throw new VM.OperationError(VM.OperationErrorType.DivisionByZero);

          return vm.getBigNumber(l, t.isInteger).dividedBy(r);
        },
      });
    else
      return this.switchNumeric(l, {
        number: (vm, l, t) => {
          if (vm.isZero(r))
            throw new VM.OperationError(VM.OperationErrorType.ModuloByZero);

        },
        bigNumber: (vm, l, t) => {
          if (vm.isZero(r))
            throw new VM.OperationError(VM.OperationErrorType.ModuloByZero);

          return l.dividedBy(vm.getBigNumber(r, t.isInteger));
        }
      });
  }

  mod(l: any, r: any): any {
    if (this.getPrecedence(l) < this.getPrecedence(r))
      return this.switchNumeric(r, {
        number: (vm, r, t) => vm.getNumber(l, t.isInteger) % r,
        bigNumber: (vm, r, t) => vm.getBigNumber(l, t.isInteger).mod(r),
      });
    else
      return this.switchNumeric(l, {
        number: (vm, l, t) => l % vm.getNumber(r, t.isInteger),
        bigNumber: (vm, l, t) => l.mod(vm.getBigNumber(r, t.isInteger)),
      });
  }

  pow(l: any, r: any): any {
    if (this.getPrecedence(l) < this.getPrecedence(r))
      return this.switchNumeric(r, {
        number: (vm, r, t) => Math.pow(vm.getNumber(l, t.isInteger), r),
        bigNumber: (vm, r, t) => vm.getBigNumber(l, t.isInteger).pow(r),
      });
    else
      return this.switchNumeric(l, {
        number: (vm, l, t) => Math.pow(l, vm.getNumber(r, t.isInteger)),
        bigNumber: (vm, l, t) => l.pow(vm.getBigNumber(r, t.isInteger)),
      });
  }


  lt(l: any, r: any): boolean {
    if (this.getPrecedence(l) < this.getPrecedence(r))
      return this.gte(r, l);
    else
      return this.switchValue(l, {
        number: (vm, l, t) => l < vm.getNumber(r, t.isInteger),
        bigNumber: (vm, l, t) => l.isLessThan(vm.getBigNumber(r, t.isInteger)),
      });
  }

  lte(l: any, r: any): boolean {
    if (this.getPrecedence(l) < this.getPrecedence(r))
      return this.gt(r, l);
    else
      return this.switchValue(l, {
        number: (vm, l, t) => l <= vm.getNumber(r, t.isInteger),
        bigNumber: (vm, l, t) => l.isLessThanOrEqualTo(vm.getBigNumber(r, t.isInteger)),
      });
  }

  gt(l: any, r: any): boolean {
    if (this.getPrecedence(l) < this.getPrecedence(r))
      return this.lte(r, l);
    else
      return this.switchValue(l, {
        number: (vm, l, t) => l > vm.getNumber(r, t.isInteger),
        bigNumber: (vm, l, t) => l.isGreaterThan(vm.getBigNumber(r, t.isInteger)),
      });
  }

  gte(l: any, r: any): boolean {
    if (this.getPrecedence(l) < this.getPrecedence(r))
      return this.lt(r, l);
    else
      return this.switchValue(l, {
        number: (vm, l, t) => l >= vm.getNumber(r, t.isInteger),
        bigNumber: (vm, l, t) => l.isGreaterThanOrEqualTo(vm.getBigNumber(r, t.isInteger)),
      });
  }

  eq(l: any, r: any): boolean {
    if (this.getPrecedence(l) < this.getPrecedence(r))
      return this.eq(r, l);
    else
      return this.switchValue(l, {
        bool: (vm, l) => l === vm.getBool(r),
        number: (vm, l, t) => l == vm.getNumber(r, t.isInteger),
        bigNumber: (vm, l, t) => l.isEqualTo(vm.getBigNumber(r, t.isInteger)),
        dateOnly: (vm, l) => l.isSame(vm.getDateOnly(r)),
        timeOnly: (vm, l) => l.isSame(vm.getTimeOnly(r)),
        dateTime: (vm, l) => l.isSame(vm.getDateTime(r, l.timezone)),
        text: (vm, l) => l.toLowerCase() == vm.getText(r)?.toLowerCase(),
      });
  }

  neq(l: any, r: any): boolean {
    return !this.eq(l, r);
  }


  and(l: any, r: any): boolean {
    return this.getBool(l, true) && this.getBool(r, true);
  }

  or(l: any, r: any): boolean {
    return this.getBool(l, true) || this.getBool(r, true);
  }


  conditional(condition: any, whenTrue: any, whenFalse: any): any {
    return this.getBool(condition, true) ? whenTrue : whenFalse;
  }


  not(r: any): boolean {
    return !this.getBool(r);
  }

  minus(r: any): any {
    return this.switchNumeric(r, {
      number: (vm, r, t) => -r,
      bigNumber: (vm, r, t) => r.negated(),
    });
  }

  plus(r: any): any {
    return this.switchNumeric(r, {
      number: (vm, r, t) => r,
      bigNumber: (vm, r, t) => r,
    });
  }

  abs(n: any): any {
    return this.switchNumeric(n, {
      number: (vm, n, t) => Math.abs(n),
      bigNumber: (vm, n, t) => n.abs(),
    });
  }


  cat(separator: any, ...args: any[]): any {
    separator = this.getText(separator);
    return args.map(a => this.getText(a)).join(separator);
  }

  with(l: any, r: any): any {
    let list = this.getList(l);

    if (list instanceof Set) {
      for (let v of list) {
        if (this.eq(v, r))
          return list;
      }

      let newList = new Set(list.values());
      newList.add(r);
      return newList;
    }
    else if (Array.isArray(list)) {
      if (!list.some(x => this.eq(x, r)))
        return list.filter(x => !this.eq(x, r));

      return list;
    }

    throw new Error(`Expected a list`);
  }

  without(l: any, r: any): any {
    let list = this.getList(l);

    if (list instanceof Set) {
      let newList = new Set(list.values());

      for (let v of newList) {
        if (this.eq(v, r)) 
          newList.delete(v);
      }

      return newList;
    }
    else if (Array.isArray(list)) {
      if (list.some(x => this.eq(x, r)))
        return list.filter(x => !this.eq(x, r));

      return list;
    }

    throw new Error(`Expected a list`);
  }

  in(l: any, r: any): any {
    let list = this.getList(l);

    if (list instanceof Set) {
      for (let v of list.values()) {
        if (this.eq(v, r))
          return true;
      }

      return false;
    }
    else if (Array.isArray(list)) {
      for (let v of list) {
        if (this.eq(v, r))
          return true;
      }

      return false;
    }

    throw new Error(`Expected a list`);
  }


  isNull(v: any): boolean {
    return isNullOrUndefined(v);
  }

  isBlank(v: any): boolean {
    return this.getText(v) === '';
  }

  isNullOrBlank(v: any): boolean {
    return this.isNull(v) || this.isBlank(v);
  }


  isTrue(v: any, conversion = false): boolean {
    return this.getBool(v, conversion) === true;
  }

  isFalse(v: any, conversion = false): boolean {
    return this.getBool(v, conversion) === false;
  }


  isNaN(v: any): boolean {
    if (v instanceof Value)
      v = v.value;

    return (v instanceof BigNumber) ? v.isNaN() : isNaN(this.getNumber(v, false, true));
  }

  isZero(v: any): boolean {
    if (v instanceof Value)
      v = v.value;

    return (v instanceof BigNumber) ? v.isZero() : this.getNumber(v, false, true) == 0;
  }


  getBool(v: any, conversion = false): boolean {
    if (v instanceof Value && v.type.isBoolean)
      v = v.value;

    if (typeof v === 'boolean')
      return v;

    if (conversion)
      return this.convertToBool(v);

    throw new Error(`Expected boolean, but got ${this.getDataType(v).title} instead.`, { cause: v });
  }


  getNumber(v: any, int?: boolean, conversion = false): number {
    if (v instanceof Value && v.type.isNumeric)
      v = v.value;

    if (typeof v === 'number')
      return int ? Math.floor(v) : v;

    if (conversion)
      return this.convertToNumber(v, int);

    throw new Error(`Expected number, but got ${this.getDataType(v).title} instead.`, { cause: v });
  }

  getBigNumber(v: any, int?: boolean, conversion = false): BigNumber {
    if (v instanceof Value && v.type.isNumeric)
      v = v.value;

    if (v instanceof BigNumber)
      return int ? v.integerValue() : v;

    if (conversion)
      return this.convertToBigNumber(v, int);

    throw new Error(`Expected number, but got ${this.getDataType(v).title} instead.`, { cause: v });
  }

  getIntNumber(v: any, conversion = false) {
    return this.getNumber(v, true, conversion);
  }

  getIntBigNumber(v: any, conversion = false) {
    return this.getBigNumber(v, true, conversion);
  }

  getDateOnly(v: any, conversion = false): DataQueryExpr.DateValue {
    if (v instanceof Value && v.type.isDateOnly)
      v = v.value;

    if (v instanceof DataQueryExpr.DateValue)
      return v;

    if (conversion)
      return this.convertToDateOnly(v);

    throw new Error(`Expected date, but got ${this.getDataType(v).title} instead.`, { cause: v });
  }

  getTimeOnly(v: any, conversion = false): DataQueryExpr.TimeValue {
    if (v instanceof Value && v.type.isTimeOnly)
      v = v.value;

    if (v instanceof DataQueryExpr.TimeValue)
      return v;

    if (conversion)
      return this.convertToTimeOnly(v);

    throw new Error(`Expected time, but got ${this.getDataType(v).title} instead.`, { cause: v });
  }

  getDateTime(v: any, tz?: string, conversion = false): DataQueryExpr.DateTimeValue {
    if (v instanceof Value && v.type.isDateOrDateTime)
      v = v.value;

    if (v instanceof DataQueryExpr.DateTimeValue) {
      v = DataQueryExpr.createDateTimeValue(v, tz);
      return v;
    }

    if (conversion)
      return this.convertToDateTime(v, tz);

    throw new Error(`Expected date & time, but got ${this.getDataType(v).title} instead.`, { cause: v });
  }

  getText(v: any, display?: DataDisplayOptions): string {
    if (v instanceof Value && v.type.isText)
      v = v.value;

    if (typeof v === 'string')
      return v;

    return this.convertToText(v, display);
  }


  getList(v: any): any[] | Set<any> {
    throw new Error(`Expected list, but got ${this.getDataType(v).title} instead.`, { cause: v });
  }

  getMap(v: any): Record<string, any> | Map<string, any> {
    throw new Error(`Expected map, but got ${this.getDataType(v).title} instead.`, { cause: v });
  }


  indexList(list: any, i: any, throwOnOutOfBounds = false): any {
    list = this.getList(list);
    i = this.getNumber(i, true);

    if (Array.isArray(list))
      return list[i];
    else if (list instanceof Set)
      return Array.from(list.values())[i];

    if (throwOnOutOfBounds)
      throw new Error(`Invalid list index operation`, { cause: [list, i] });
    else
      return null;
  }

  indexMap(map: any, k: any, throwOnNotFound = false): any {
    map = this.getMap(map);
    k = this.getText(k);

    if (map instanceof Map)
      return map.get(k);
    else if (typeof map === 'object')
      return map[k];

    if (throwOnNotFound)
      throw new Error(`Invalid map index operation`, { cause: [map, k] });
    else
      return null;
  }


  makeList(...items: any[]): any[] {
    return items;
  }

  makeMap(...items: [any, any][]): Record<string, any> {
    let map = {};

    for (let i = 0; i < items.length; ++i) {
      let k = items[i * 2 + 0];
      let v = items[i * 2 + 1];

      map[this.getText(k)] = v;
    }

    return map;
  }


  convertToBool(v: any): boolean {
    if (isNullOrUndefined(v))
      return false;

    if (v instanceof Value) {
      if (v.type.isBoolean)
        return v.value;

      v = v.value;
    }

    if (typeof v === 'boolean')
      return v;

    if (typeof v === 'number')
      return v !== 0;

    if (typeof v === 'bigint')
      return v !== 0n;

    if (typeof v === 'string') {
      if (v === '')
        return false;

      let b = v.toUpperCase() === 'TRUE'  ? true :
              v.toUpperCase() === 'FALSE' ? false : undefined;

      if (b === undefined)
        throw new Error(`Invalid boolean value '${v}'`, { cause: v });

      return b;
    }

    throw new Error(`Invalid conversion to boolean from ${this.getDataType(v).title}`, { cause: v });
  }

  convertToNumber(v: any, int?: boolean): number {
    if (isNullOrUndefined(v))
      return NaN;

    if (v instanceof Value) {
      if ((int ? v.type.isInteger : v.type.isNumeric) && typeof v.value === 'number')
        return v.value;

      v = v.value;
    }

    if (typeof v === 'number')
      return int ? Math.floor(v) : v;

    let n: number;

    if (typeof v === 'boolean')
      return v ? 1 : 0;

    if (typeof v === 'string')
      return int ? parseInt(v) : parseFloat(v);

    if (typeof v === 'bigint')
      n = Number(v);
    else if (v instanceof BigNumber)
      n = v.toNumber();

    if (n !== undefined) {
      if (int)
        n = Math.floor(n);

      return n;
    }

    throw new Error(`Invalid conversion to ${int ? 'integer' : 'number'} from ${this.getDataType(v).title}`, { cause: v });
  }

  convertToBigNumber(v: any, int?: boolean): BigNumber {
    if (isNullOrUndefined(v))
      return new BigNumber(NaN);

    if (v instanceof Value) {
      if ((int ? v.type.isInteger : v.type.isNumeric) && v.value instanceof BigNumber)
        return v.value;

      v = v.value;
    }

    if (v instanceof BigNumber)
      return int ? v.integerValue() : v;

    if (typeof v === 'boolean')
      return new BigNumber(v ? 1 : 0);

    if (typeof v === 'number' || typeof v === 'bigint' || typeof v === 'string') {
      let n = new BigNumber(v);

      if (n !== undefined) {
        if (int)
          n = n.integerValue(BigNumber.ROUND_FLOOR);

        return n;
      }
    }

    throw new Error(`Invalid conversion to ${int ? 'integer' : 'number'} from ${this.getDataType(v).title}`, { cause: v });
  }

  convertToDateOnly(v: any): DataQueryExpr.DateValue {
    if (isNullOrUndefined(v))
      return null;

    if (v instanceof Value)
      v = v.value;

    if (v instanceof DataQueryExpr.DateValue)
      return v;

    if (v instanceof DataQueryExpr.DateTimeValue)
      return v.date;

    let d: Date;

    if (typeof v === 'string')
      d = new Date(v);
    else if (moment.isMoment(v))
      d = v.toDate();
    else if (v instanceof Date)
      d = v;

    if (d !== undefined) {
      return DataQueryExpr.createDateValue(d);
    }

    throw new Error(`Invalid conversion to date from ${this.getDataType(v).title}`, { cause: v });
  }

  convertToTimeOnly(v: any): DataQueryExpr.TimeValue {
    if (isNullOrUndefined(v))
      return null;

    if (v instanceof Value)
      v = v.value;

    if (v instanceof DataQueryExpr.TimeValue)
      return v;

    if (v instanceof DataQueryExpr.DateValue)
      return DataQueryExpr.TimeValue.midnight;

    if (v instanceof DataQueryExpr.DateTimeValue)
      return v.time;

    let d: Date;

    if (typeof v === 'string')
      d = new Date(v);
    else if (moment.isMoment(v))
      d = v.toDate();
    else if (v instanceof Date)
      d = v;

    if (d !== undefined) {
      return DataQueryExpr.createTimeValue(d);
    }

    throw new Error(`Invalid conversion to time from ${this.getDataType(v).title}`, { cause: v });
  }

  convertToDateTime(v: any, tz: string): DataQueryExpr.DateTimeValue {
    if (isNullOrUndefined(v))
      return null;

    if (v instanceof Value)
      v = v.value;

    if (v instanceof DataQueryExpr.DateTimeValue)
      return v;

    if (v instanceof DataQueryExpr.DateValue)
      return DataQueryExpr.createDateTimeValue(v, DataQueryExpr.TimeValue.midnight, tz);

    let d: Date;

    if (typeof v === 'string')
      d = new Date(v);
    else if (moment.isMoment(v))
      d = v.toDate();
    else if (v instanceof Date)
      d = v;

    if (d !== undefined) {
      return DataQueryExpr.createDateTimeValue(d, tz);
    }

    throw new Error(`Invalid conversion to date & time from ${this.getDataType(v).title}`, { cause: v });
  }

  convertToText(v: any, display?: DataDisplayOptions) {
    let dataType = this.getDataType(v);
    let [value, isAggregated, aggregation] =
      (v instanceof Value) 
        ? [v.value, v.isAggregated, v.aggregation] 
        : [v, false, null];

    return this.evaluator.dataFormatter.formatForDisplay(value, {
      dataType, 
      isAggregated,
      aggregation,
      display
    });
  }

  convert(v: any, targetType: DataType) {
    let valueType = this.getDataType(v);

    // Conversion to text is meant to trigger formatting logic
    if (targetType.isText)
      return this.convertToText(v);

    // Other conversions don't require any additional logic if the same is already exact
    if (valueType === targetType)
      return v;

    if (targetType.isBoolean)
      return this.convertToBool(v);

    if (targetType.isInteger)
      return this.convertToBigNumber(v, true);

    if (targetType.isNumber)
      return this.convertToBigNumber(v, false);

    if (targetType.isDecimal)
      return this.convertToBigNumber(v, false);

    if (targetType.isDateOnly)
      return this.convertToDateOnly(v);

    if (targetType.isTimeOnly)
      return this.convertToTimeOnly(v);

    if (targetType.isDateTime)
      return this.convertToDateTime(v, this.ident(this.symbols.TIMEZONE));

    throw new Error(`Unknown conversion`);
  }


  getDisplayOptions(dataType: DataType, display?: DataDisplayOptions) {
    return {
      // TODO: getDisplayOptions
      ...display
    };
  }


  toInt(v: any) {
    if (v instanceof Value) {
      if (v.type.isInteger)
        return v;

      v = v.value;
    }

    if (v instanceof BigNumber)
      return v.integerValue();
    else if (typeof v === 'bigint')
      return new BigNumber(v).integerValue();
    else
      return Math.floor(v);
  }


  switchValue(v: any, callbacks: {
    bool?: (vm: VM, v: boolean, t?: DataType) => any;
    number?: (vm: VM, v: number, t?: DataType) => any;
    bigNumber?: (vm: VM, v: BigNumber, t?: DataType) => any;
    dateOnly?: (vm: VM, v: DataQueryExpr.DateValue, t?: DataType) => any;
    timeOnly?: (vm: VM, v: DataQueryExpr.TimeValue, t?: DataType) => any;
    dateTime?: (vm: VM, v: DataQueryExpr.DateTimeValue, t?: DataType) => any;
    text?: (vm: VM, v: string, t?: DataType) => any;
    fallback?: (vm: VM, v: any, t?: DataType) => any;
  }) {
    if (v instanceof Value) {
      if (v.type.isNumeric) {
        return this.switchNumeric(v, {
          number: callbacks.number ?? callbacks.fallback,
          bigNumber: callbacks.bigNumber ?? callbacks.fallback,
        });
      }

      if (v.type.isBoolean)
        return (callbacks.bool ?? callbacks.fallback)(this, v.value, v.type);

      if (v.type.isDateOnly)
        return (callbacks.dateOnly ?? callbacks.fallback)(this, v.value, v.type);

      if (v.type.isTimeOnly)
        return (callbacks.timeOnly ?? callbacks.fallback)(this, v.value, v.type);

      if (v.type.isDateTime)
        return (callbacks.dateTime ?? callbacks.fallback)(this, v.value, v.type);

      if (v.type.isText)
        return (callbacks.text ?? callbacks.fallback)(this, v.value, v.type);

      if (callbacks.fallback)
        return callbacks.fallback(this, v.value, v.type);
    }
    else {
      if (typeof v === 'number' || typeof v === 'bigint' || v instanceof BigNumber) {
        return this.switchNumeric(v, {
          number: callbacks.number ?? callbacks.fallback,
          bigNumber: callbacks.bigNumber ?? callbacks.fallback,
        });
      }

      let t = this.getDataType(v);

      if (typeof v === 'boolean')
        return (callbacks.bool ?? callbacks.fallback)(this, v, t);

      if (v instanceof DataQueryExpr.DateValue)
        return (callbacks.dateOnly ?? callbacks.fallback)(this, v, t);

      if (v instanceof DataQueryExpr.TimeValue)
        return (callbacks.timeOnly ?? callbacks.fallback)(this, v, t);

      if (v instanceof v || moment.isMoment(v) || v instanceof DataQueryExpr.DateTimeValue)
        return (callbacks.dateTime ?? callbacks.fallback)(this, v, t);

      if (typeof v === 'string')
        return (callbacks.text ?? callbacks.fallback)(this, v, t);

      if (callbacks.fallback)
        return callbacks.fallback(this, v, t);
    }

    throw new Error(`Unexpected value`, { cause: v });
  }

  switchNumeric(v: any, callbacks: {
    number: (vm: VM, n: number, t?: DataType) => any;
    bigNumber: (vm: VM, n: BigNumber, t?: DataType) => any;
    fallback?: (vm: VM, v: any, t?: DataType) => any;
  }) {
    let t = DataType.Number;
    let r;

    if (v instanceof Value) {
      v = v.value;
      t = v.type;
    } else {
      t = this.getDataType(v);
    }

    if (typeof v === 'number') {
      r = callbacks.number(this, v, t);
      return this.getNumber(r, t.isInteger, false);
    }
    else {
      if (v instanceof BigNumber) {
        r = callbacks.bigNumber(this, v, t);
      }
      else {
        let n = this.convertToBigNumber(v, false);
        if (!n) {
          if (!callbacks.fallback)
            throw new Error(`Unexpected value`, { cause: v });

          r = callbacks.fallback(this, v, t);
        }

        r = this.switchNumeric(n, callbacks);
      }

      return this.getBigNumber(r, t.isInteger, false);
    }
  }


  selectNumbers(v: any[]): number[] | BigNumber[] {
    v = [...v];

    let allNumber = !v.some(x => !(typeof x === 'number'));
    if (!allNumber) {
      for (let i = 0; i < v.length; ++i)
        v[i] = this.convertToBigNumber(v[i], false);
    }

    return v;
  }


  selectByPrecedence(a: any, b: any) {
    return this.getPrecedence(a) < this.getPrecedence(b) ? b : a;
  }

  getPrecedence(v: any) {
    let type = this.getDataType(v);
    if (!type)
      return Number.MIN_SAFE_INTEGER;

    let p = type.precedence;

    if (type.isNumeric) {
      v = (v instanceof Value) ? v.value : v;

      if (typeof v === 'bigint')
        p += 1;
      else if (v instanceof BigNumber)
        p += 2;
    }

    return p;
  }

  getDataType(v: any): DataType | null {
    if (isNullOrUndefined(v))
      return null;

    if (v instanceof Value)
      return v.type;

    if (typeof v === 'boolean')
      return DataType.Boolean;

    if (typeof v === 'bigint')
      return DataType.Integer;

    if (typeof v === 'number')
      return DataType.Number;

    if (v instanceof BigNumber)
      return DataType.Decimal;

    if (v instanceof DataQueryExpr.DateValue)
      return DataType.DateOnly;
      
    if (v instanceof DataQueryExpr.TimeValue)
      return DataType.TimeOnly;

    if (v instanceof Date || moment.isMoment(v) || v instanceof DataQueryExpr.DateTimeValue)
      return DataType.DateTime;

    if (typeof v === 'string')
      return DataType.Text;

    if (Array.isArray(v) || v instanceof Set)
      return DataType.List;

    if (typeof v === 'object' || v instanceof Map)
      return DataType.Map;

    throw new Error(`Unsupported value '${typeof v}'`, { cause: v });
  }


  getIdent(v: any): IdentifierSymbol {
    let ident = (v instanceof IdentifierSymbol) ? v : null;

    if (!ident)
      throw new Error(`Expected an identifier`, { cause: v });

    return ident;
  }

  getIdentOrText(v: any): IdentifierSymbol | string {
    let result =
      (v instanceof IdentifierSymbol)       ? v :
      (typeof v === 'string')               ? v :
      (v instanceof Value && v.type.isText) ? v.value : null;

    if (!result)
      throw new Error(`Expected an identifier or a constant string`, { cause: v });

    return result;
  }


  getDataTypeFromSymbol(v: any, throwOnNull?: boolean): DataType {
    if (throwOnNull === undefined)
      throwOnNull = true;

    let t = this.getIdentOrText(v);
    if (!t) {
      if (throwOnNull)
        throw new Error(`Expected a data type name`);

      return null;
    }

    if (t instanceof IdentifierSymbol) {
      if (t === this.symbols.BOOLEAN)
        return DataType.Boolean;

      if (t === this.symbols.INTEGER)
        return DataType.Integer;

      if (t === this.symbols.NUMBER)
        return DataType.Number;

      if (t === this.symbols.DECIMAL)
        return DataType.Decimal;

      if (t === this.symbols.DATEONLY)
        return DataType.DateOnly;

      if (t === this.symbols.TIMEONLY)
        return DataType.TimeOnly;

      if (t === this.symbols.DATETIME)
        return DataType.DateTime;

      if (t === this.symbols.TEXT)
        return DataType.Text;
    }
    else if (typeof t === 'string') {
      t = t.toUpperCase();

      if (['BOOL', 'BOOLEAN'].includes(t))
        return DataType.Boolean;

      if (['INT', 'INTEGER'].includes(t))
        return DataType.Integer;

      if (['FLOAT', 'DOUBLE', 'REAL', 'NUMBER'].includes(t))
        return DataType.Number;

      if (['NUMERIC', 'DECIMAL'].includes(t))
        return DataType.Decimal;

      if (['DATE', 'DATEONLY'].includes(t))
        return DataType.DateOnly;

      if (['DATETIME'].includes(t))
        return DataType.DateTime;

      if (['TIME', 'TIMEONLY'].includes(t))
        return DataType.TimeOnly;

      if (['TEXT', 'STRING'].includes(t))
        return DataType.Text;
    }

    throw new Error(`Invalid data type name '${t}'`, { cause: v });
  }
}

export namespace VM {
  export enum OperationErrorType {
    DivisionByZero,
    ModuloByZero,
    UnhandledError
  }

  export class OperationError extends Error {
    constructor(public readonly type: OperationErrorType) {
      super();
    }
  }
}
